import { useMemo } from "react";
import { routes } from "@/router";
import { useTranslation } from "react-i18next";
import { HiOutlineEmojiHappy } from "react-icons/hi";
import { HiOutlineArrowSmallLeft, HiOutlineMagnifyingGlass, HiOutlinePencil } from "react-icons/hi2";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import { useGetCampaignInfo } from "@/hooks/api/predictive/campaign/useGetCampaignInfo";
import { useGetCampaignLists } from "@/hooks/api/predictive/campaign/useGetCampaignLists";
import { useTableConfig } from "@/hooks/table/useTableConfig";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import Button from "@/components/atoms/Button";
import Input from "@/components/atoms/input/Input";
import FeaturedText from "@/components/molecules/FeaturedText";
import CampaignStatusBadge from "@/components/molecules/predictive/CampaignStatusBadge";
import NoListResults from "@/components/organisms/list/NoListResults";
import { aiLeadsColumns, aiLeadsColumnsDef } from "@/components/organisms/predictive/aiLeadsColumns";
import Table, { TableType } from "@/components/organisms/table/Table";

const CampaignLists = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { campaignId, productId } = useParams();

  const { data: campaign, isLoading: isLoadingCampaign } = useGetCampaignInfo({ campaignId });

  const { query, setDebounceQuery, isTyping } = useDebounceQuery();
  const { data, isLoading, isFetched, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetCampaignLists({
    campaignId: campaignId!,
    query: query || undefined,
  });
  const flattenedData = useMemo(() => data?.pages?.flatMap((page) => page.data) || [], [data]);

  const columnOrder = useMemo(() => Object.values(aiLeadsColumns(t)).map((column) => column.id), [t]);
  const columnNames = useMemo(() => aiLeadsColumns(t), [t]);
  const columns = useMemo(() => aiLeadsColumnsDef({ t, campaignId: campaignId!, productId: productId! }), [t]);

  const totalResults = useMemo(() => data?.pages[0].info?.items_total || 0, [data?.pages]);
  const loadingRowCount = useMemo(() => (isTyping ? Math.min(totalResults, 25) : 5), [isTyping, totalResults]);

  const columnValues = useMemo(() => Object.values(columnNames), [columnNames]);

  const tableConfig = useTableConfig({
    columns: columnValues,
    initialColumnOrder: columnOrder,
  });

  return (
    <div className={"flex h-screen flex-grow flex-col overflow-y-auto overflow-x-hidden px-6 pb-4 pt-5"}>
      <div className={"mb-2 flex items-start justify-between"}>
        <div className={"flex items-center"}>
          <Button
            leadingIcon={<HiOutlineArrowSmallLeft className={"size-5"} />}
            onClick={() => history.back()}
            size={"xs"}
            className={"!py-0 !pl-1"}
            color={"natural"}
            variants={"text"}
          />
          <h2 className={"mt-1 flex items-center gap-2"}>
            {isLoadingCampaign || !campaign ? (
              <div className="h-7 w-20 animate-pulse rounded-full bg-gray-300" />
            ) : (
              <span className={"flex items-center space-x-2"}>
                <div className={"text-2xl font-semibold leading-none"}>{campaign.name}</div>
                <CampaignStatusBadge campaign={campaign} />
              </span>
            )}
          </h2>
        </div>

        <div>
          <Button
            onClick={() => navigate(generatePath(routes.campaign.update, { productId, campaignId }))}
            color={"natural"}
            trailingIcon={<HiOutlinePencil className={"size-5"} />}
            label={t("edit")}
          />
        </div>
      </div>

      {campaign?.description && (
        <div className={"mb-4 w-3/4 lg:w-1/2"}>
          <div className={"text-sm text-quaternary"}>{campaign?.description}</div>
        </div>
      )}

      <div className={"mb-6 flex gap-3"}>
        <div className={"max-w-56"}>
          {((isFetched && flattenedData.length !== 0) || query !== "") && (
            <Input
              leftPlaceholder={<HiOutlineMagnifyingGlass className={"size-5"} />}
              sizing={"xs"}
              onChange={(e) => setDebounceQuery(e.target.value)}
              placeholder={t("static.search")}
            />
          )}
        </div>
      </div>

      {isFetched && flattenedData.length === 0 ? (
        query ? (
          <NoListResults query={query} />
        ) : (
          <WaitForListFeaturedText />
        )
      ) : (
        <Table
          allColumns={columns}
          data={flattenedData}
          tableType={TableType.LISTS}
          columnNames={columnNames}
          canReorderColumns={false}
          canSelectRows={false}
          canReorderRows={false}
          isLoading={isLoading || isTyping}
          showHeaderWhileLoading
          loadMore={fetchNextPage}
          hasMoreData={hasNextPage}
          loadingRowCount={loadingRowCount}
          isFetchingNextPage={isFetchingNextPage}
          hasContextMenu={false}
          {...tableConfig}
          tableOptions={{
            enableHiding: false,
            columnResizeDirection: "ltr",
            columnResizeMode: "onChange",
            enableRowSelection: true,
            getRowId: (row) => row.id,
          }}
        />
      )}
    </div>
  );
};

export default CampaignLists;

const WaitForListFeaturedText = () => {
  return (
    <FeaturedText
      featuredIcon={{ icon: HiOutlineEmojiHappy, size: "lg" }}
      size={"lg"}
      heading={"Sit Back While We Work"}
      body={
        "Once your campaign is submitted and processed, our data experts get to work. Every lead is manually reviewed and verified to ensure the highest quality. No mass scraping, just handpicked, relevant companies that match your criteria. You can expect your first curated list in about two weeks."
      }
    />
  );
};
